/**
 * React hook over the server-controlled trust feature flags.
 *
 * The flags live in the `trust_feature_flags` table and are all off by default,
 * so every trust surface stays hidden until an operator enables it server-side —
 * no frontend deploy is needed to roll a slice out or back.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  loadTrustFlags,
  type TrustFeatureFlagKey,
} from '../../../shared/lib/trustFlags';

interface UseTrustFlagsResult {
  flags: Record<string, boolean>;
  loading: boolean;
  isEnabled: (key: TrustFeatureFlagKey) => boolean;
  refresh: () => void;
}

export function useTrustFlags(): UseTrustFlagsResult {
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (force = false) => {
    setLoading(true);
    try {
      const next = await loadTrustFlags(force);
      setFlags(next);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    void (async () => {
      const next = await loadTrustFlags();
      if (active) {
        setFlags(next);
        setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const isEnabled = useCallback(
    (key: TrustFeatureFlagKey) => Boolean(flags[key]),
    [flags]
  );

  return { flags, loading, isEnabled, refresh: () => void load(true) };
}
